"use client";

import { useState, useEffect, useRef } from "react";
import type { CategoryDto } from "@/lib/api";
import { categoryColor } from "@/lib/category-colors";


/**
 * Dropdown "Approfondimenti" nella nav — con sottocategorie colorate.
 * Le categorie vengono passate dal layout (server-side).
 */
export function ApprofondimentiDropdown({
  mobile = false,
  categories = [],
}: {
  mobile?: boolean;
  categories?: CategoryDto[];
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Chiudi cliccando fuori
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleMouseEnter = () => {
    if (mobile) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    if (mobile) return;
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };
  /* ── Mobile: accordion ── */
  if (mobile) {
    return (
      <div ref={ref}>
        <button
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-[#1a3a5c] hover:bg-neutral-50"
        >
          Approfondimenti
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {open && (
          <div className="bg-neutral-50 border-t border-neutral-100 py-1">
            {categories.map((c) => (
              <a
                key={c.id}
                href={`/approfondimenti?category=${c.slug}`}
                className="flex items-center gap-2.5 px-5 py-2.5 text-sm hover:bg-neutral-100 transition-colors"
              >
                <span
                  className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${categoryColor(c.slug)}`}
                >
                  {c.name}
                </span>
              </a>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── Desktop: hover dropdown ── */
  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 px-3 py-2 text-[#1a3a5c] hover:text-[#4a90c4] transition-colors uppercase relative after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[2px] after:bg-[#4a90c4] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left"
      >
        Approfondimenti
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-3.5 w-3.5 opacity-60 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl border border-neutral-200 py-2 z-50 animate-fade-in">
          {categories.map((c) => (
            <a
              key={c.id}
              href={`/approfondimenti?category=${c.slug}`}
              className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-neutral-50 transition-colors group"
            >
              <span
                className={`inline-block w-3 h-3 rounded-full shrink-0 shadow-sm ${categoryColor(c.slug).split(" ")[0]}`}
                style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
              />
              <span className={`font-semibold ${categoryColor(c.slug).split(" ")[1]} group-hover:opacity-80 transition-opacity`}>
                {c.name}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
